import { ApolloClient } from '@apollo/client';
import Decimal from 'decimal.js';
import { compact, defaults, first, isNull, omitBy } from 'lodash';

import { BOTH_GRANTORS_SENTINEL } from '@/hooks/useDualGrantorOptions';
import {
  InsurancePolicyKind,
  InsurancePolicyPremiumPaymentSourceKind,
  InsurancePolicyPremiumPaymentTransferTaxKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';

import { EntityType } from '../types/EntityType';
import {
  InsurancePolicies_InsurancePolicyFragment,
  InsurancePoliciesDocument,
  InsurancePoliciesQuery,
} from './graphql/InsurancePolicies.generated';
import {
  defaultPolicy,
  InsurancePolicyDetails,
  InsurancePolicyDetailsSubformType,
  VariantType,
} from './InsurancePolicyDetailsSubform.types';

export function mapInsurancePolicy(
  policy: InsurancePolicies_InsurancePolicyFragment
): InsurancePolicyDetails {
  const policyHolderIDs = compact(
    policy.policyHolders?.map<string>((holder) => holder?.id)
  );

  if (!policyHolderIDs.length) {
    policyHolderIDs.push('');
  }
  if (
    policyHolderIDs.length === 1 &&
    policy.kind !== InsurancePolicyKind.Term
  ) {
    policyHolderIDs.push('');
  }

  const documentIds = compact(
    policy.documents?.map<string>((document) => document?.id)
  );

  let premiumPaymentTransferTaxKind: string | null = null;
  let premiumPaymentGiftingGrantorIDs: string[] = [];

  if (!policy.premiumPaymentTransferTaxKind) {
    premiumPaymentTransferTaxKind = null;
  } else if (
    policy.premiumPaymentTransferTaxKind ===
    InsurancePolicyPremiumPaymentTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers
  ) {
    premiumPaymentTransferTaxKind =
      InsurancePolicyPremiumPaymentTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers;
    premiumPaymentGiftingGrantorIDs = [];
  } else if (
    policy.premiumPaymentTransferTaxKind ===
      InsurancePolicyPremiumPaymentTransferTaxKind.GrantorTaxableGift &&
    policy.premiumPaymentGiftingGrantors &&
    policy.premiumPaymentGiftingGrantors.length > 1
  ) {
    premiumPaymentTransferTaxKind = BOTH_GRANTORS_SENTINEL;
    premiumPaymentGiftingGrantorIDs =
      policy.premiumPaymentGiftingGrantors?.map((grantor) => grantor?.id) || [];
  } else if (
    policy.premiumPaymentTransferTaxKind ===
      InsurancePolicyPremiumPaymentTransferTaxKind.GrantorTaxableGift &&
    policy.premiumPaymentGiftingGrantors &&
    policy.premiumPaymentGiftingGrantors.length === 1
  ) {
    const firstGrantor = first(policy.premiumPaymentGiftingGrantors);
    premiumPaymentTransferTaxKind = firstGrantor?.id || null;
    if (firstGrantor) {
      premiumPaymentGiftingGrantorIDs = [firstGrantor.id];
    }
  }

  let premiumPaymentSourceID: string = '';
  switch (policy.premiumPaymentSourceKind) {
    case InsurancePolicyPremiumPaymentSourceKind.Self:
      premiumPaymentSourceID = InsurancePolicyPremiumPaymentSourceKind.Self;
      break;
    case InsurancePolicyPremiumPaymentSourceKind.Entity:
      premiumPaymentSourceID = policy.premiumPaymentSourceEntityID || '';
      break;
    case InsurancePolicyPremiumPaymentSourceKind.Individual:
      premiumPaymentSourceID = policy.premiumPaymentSourceIndividualID || '';
      break;
    case InsurancePolicyPremiumPaymentSourceKind.TestamentaryEntity:
      premiumPaymentSourceID =
        policy.premiumPaymentSourceTestamentaryEntityID || '';
      break;
    case null:
      break;
    case undefined:
      break;
    default: {
      throw new UnreachableError({
        case: policy.premiumPaymentSourceKind,
        message: 'Invalid premium payment source kind',
      });
    }
  }

  const output: InsurancePolicyDetails = defaults(
    omitBy(
      {
        policyId: policy.id,
        integrationInsuranceId: policy.integrationInsurance?.id ?? null,
        policyType: policy.kind,
        isConvertible: policy.survivorship,
        conversionDate: policy.conversionDate,
        insuranceCarrier: policy.carrierName,
        policyNumber: policy.policyNumber,
        policyHolderIDs,
        deathBenefitAmount: policy.deathBenefitAmount,
        policyStartDate: policy.startDate,
        termLength: policy.termDurationYears,
        premiumAmount: policy.premiumAmount,
        premiumFrequency: policy.premiumFrequency,
        premiumInitialDueDate: policy.initialPremiumDueDate,
        premiumPaymentSourceID,
        premiumPaymentTransferTaxKind,
        premiumPaymentGiftingGrantorIDs,
        initialPremiumPaymentGiftingGrantorIDs: premiumPaymentGiftingGrantorIDs,
        notes: policy.notes,
        documentIds,
        initialDocumentIDs: documentIds,
        initialPolicyHolderIDs: policyHolderIDs,
        cashValue: policy.cashValue,
        cashValueDate: policy.cashValueDate,
        loanBalanceOutstanding: policy.loanBalanceOutstanding,
      },
      isNull
    ),
    defaultPolicy
  );

  if (policy.kind === InsurancePolicyKind.Term) {
    output.isConvertible = !!output.conversionDate;
  }

  return output;
}

function dataToForm(
  data: InsurancePoliciesQuery
): InsurancePolicyDetailsSubformType {
  if (data.node?.__typename !== 'Entity') {
    throw new Error('Unexpected node type');
  }

  if (!('policies' in data.node.subtype)) {
    throw new Error(`Unexpected subtype ${data.node.subtype.__typename}`);
  }

  const crummeyWithdrawalPeriodDays: Decimal | null =
    data?.node?.subtype?.__typename === 'ILITTrust' &&
    !!data.node.subtype.crummeyWithdrawalPeriodDays
      ? new Decimal(data?.node.subtype.crummeyWithdrawalPeriodDays)
      : null;

  const policies: InsurancePolicies_InsurancePolicyFragment[] =
    data?.node?.subtype?.policies || [];

  return {
    policies: policies.map(mapInsurancePolicy),
    initialPolicyIDs: policies.map((policy) => policy.id),
    crummeyWithdrawalPeriodDays,
  };
}

export async function insurancePolicyDataFetcher(
  apolloClient: ApolloClient<object>,
  nodeId: string
): Promise<InsurancePolicyDetailsSubformType> {
  const { data, error } = await apolloClient.query<InsurancePoliciesQuery>({
    query: InsurancePoliciesDocument,
    variables: { nodeId },
  });

  if (error) {
    diagnostics.error(
      'Could not fetch data for insurance policies form',
      error
    );
    throw error;
  }
  return dataToForm(data);
}

export function getInsurancePolicyVariant(type: EntityType): VariantType {
  if (type === 'ilit') {
    return 'ilit';
  }

  // Currently, this is just ilit
  return 'default';
}
